import type { KitchenBoardPastPlate } from "@/lib/api/types";
import type { PlateLane } from "./lateness";

/**
 * A VOIDED PLATE, STRUCK THROUGH ON ITS TICKET (B5, D168).
 *
 * The payload's `recentlyVoided` holds plates voided after firing (10 min, or
 * the READY age-off N when longer — P8; OPEN bills). The board draws each on
 * its own ticket, struck through with «ملغى», for five minutes after the
 * void — long enough for the cook to see it, short enough not to clutter.
 * Its lane comes from its STAMPS, never its state (which is VOIDED):
 * `readyAt` → ready, `preparingAt` → cooking, else new — the lane the cook
 * last saw it in. Pure.
 */

export const VOID_STRIKE_MINUTES = 5;

/** The lane a voided plate was in when it was voided. */
export function voidLane(plate: Pick<KitchenBoardPastPlate, "readyAt" | "preparingAt">): PlateLane {
  if (plate.readyAt) return "READY";
  if (plate.preparingAt) return "PREPARING";
  return "QUEUED";
}

/** Whether a voided plate is still drawn: within five minutes of `voidedAt`. */
export function isVoidStruck(plate: Pick<KitchenBoardPastPlate, "voidedAt">, now: number): boolean {
  if (!plate.voidedAt) return false;
  const at = new Date(plate.voidedAt).getTime();
  if (Number.isNaN(at)) return false;
  return now - at <= VOID_STRIKE_MINUTES * 60_000;
}

/** The voids still drawn, oldest first as the server sent them. */
export function struckVoids<T extends Pick<KitchenBoardPastPlate, "voidedAt">>(
  plates: readonly T[] | null | undefined,
  now: number,
): T[] {
  return (plates ?? []).filter((plate) => isVoidStruck(plate, now));
}
